// Converts a Date to "yyyy-MM-dd" using LOCAL time.
// date.toISOString().slice(0,10) is intentionally not used - it uses UTC,
// so e.g. after 22-23h in the evening (depending on the season) it would return "tomorrow's"
// date instead of today's.
export function toDateInputValue(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Converts a "yyyy-MM-dd" string back to a Date (local, without a UTC shift).
export function fromDateInputValue(value) {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function todayInputValue() {
  return toDateInputValue(new Date());
}

/**
 * Quick periods for the dashboard. Returns {startDate, endDate} for the selected type.
 */
export function getQuickPeriodRange(type) {
  const today = new Date();
  const start = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  switch (type) {
    case "juce": {
      const y = start(today);
      y.setDate(y.getDate() - 1);
      return { startDate: toDateInputValue(y), endDate: toDateInputValue(y) };
    }
    case "nedelja": {
      const d = start(today);
      const dayOfWeek = (d.getDay() + 6) % 7; // Monday = 0
      const monday = new Date(d);
      monday.setDate(d.getDate() - dayOfWeek);
      return { startDate: toDateInputValue(monday), endDate: toDateInputValue(today) };
    }
    case "mesec": {
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: toDateInputValue(firstOfMonth), endDate: toDateInputValue(today) };
    }
    case "prosli_mesec": {
      const firstOfPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastOfPrevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return { startDate: toDateInputValue(firstOfPrevMonth), endDate: toDateInputValue(lastOfPrevMonth) };
    }
    case "danas":
    default:
      return { startDate: toDateInputValue(today), endDate: toDateInputValue(today) };
  }
}

/**
 * Safely moves a date back by N months, clamping the day if the target
 * month has fewer days (e.g. 31.3 - 1 month -> 28.2, not 3.3 as with the naive
 * Date.setMonth() which would "overflow" into the next month).
 */
function subtractMonths(date, months) {
  const day = date.getDate();
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setMonth(d.getMonth() - months);
  const daysInTargetMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, daysInTargetMonth));
  return d;
}

function isFullCalendarMonth(start, end) {
  const lastDay = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
  return (
    start.getDate() === 1 &&
    end.getDate() === lastDay &&
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth()
  );
}

/**
 * The previous period for comparison on the dashboard - aware of the period type,
 * not just "the same number of days back" (that would compare e.g. March, 31 days, with
 * 29.1-28.2 instead of with the whole of February).
 */
export function getPreviousPeriodRange(startDate, endDate, periodType) {
  const start = fromDateInputValue(startDate);
  const end = fromDateInputValue(endDate);

  if (periodType === "danas" || periodType === "juce") {
    const prevStart = new Date(start);
    prevStart.setDate(prevStart.getDate() - 1);
    return { startDate: toDateInputValue(prevStart), endDate: toDateInputValue(prevStart) };
  }

  if (periodType === "nedelja") {
    const prevStart = new Date(start);
    prevStart.setDate(prevStart.getDate() - 7);
    const prevEnd = new Date(end);
    prevEnd.setDate(prevEnd.getDate() - 7);
    return { startDate: toDateInputValue(prevStart), endDate: toDateInputValue(prevEnd) };
  }

  // "mesec" / "prosli_mesec" / "prilagodi": a whole calendar month -> the previous
  // whole calendar month; anything else -> move both dates back by exactly one month.
  if (isFullCalendarMonth(start, end)) {
    const prevMonthStart = subtractMonths(start, 1);
    const lastDay = new Date(prevMonthStart.getFullYear(), prevMonthStart.getMonth() + 1, 0).getDate();
    const prevMonthEnd = new Date(prevMonthStart.getFullYear(), prevMonthStart.getMonth(), lastDay);
    return { startDate: toDateInputValue(prevMonthStart), endDate: toDateInputValue(prevMonthEnd) };
  }

  return {
    startDate: toDateInputValue(subtractMonths(start, 1)),
    endDate: toDateInputValue(subtractMonths(end, 1)),
  };
}

/**
 * Moves a date back by N years, clamping the day if the target month is shorter
 * (29.2.2028 minus 1 year -> 28.2.2027, not 1.3.2027).
 */
function subtractYears(date, years) {
  const d = new Date(date.getFullYear() - years, date.getMonth(), 1);
  const daysInTargetMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(date.getDate(), daysInTargetMonth));
  return d;
}

/**
 * The same date range one year earlier: today -> the same day last year, yesterday -> yesterday's date
 * last year, a week or any custom range -> the same calendar dates last year. A whole calendar
 * month maps to the whole same month last year (Feb 2028 with 29 days vs Feb 2027 with 28 days).
 */
export function getYearAgoRange(startDate, endDate) {
  const start = fromDateInputValue(startDate);
  const end = fromDateInputValue(endDate);
  const prevStart = subtractYears(start, 1);
  let prevEnd = subtractYears(end, 1);
  if (isFullCalendarMonth(start, end)) {
    prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 1, 0);
  }
  return { startDate: toDateInputValue(prevStart), endDate: toDateInputValue(prevEnd) };
}

/** Comparison range for the dashboard: mode "previous" (previous period) or "year" (same dates a year ago). */
export function getComparisonRange(startDate, endDate, periodType, mode) {
  return mode === "year" ? getYearAgoRange(startDate, endDate) : getPreviousPeriodRange(startDate, endDate, periodType);
}

/** "2026-10-05" -> "05.10.2026"; a range -> "05.10.2026 - 11.10.2026" (a single day is shown once). */
export function formatDateRange(startDate, endDate) {
  const fmtOne = (iso) => {
    const [y, m, d] = String(iso || "").split("-");
    return y && m && d ? `${d}.${m}.${y}` : "";
  };
  return startDate === endDate ? fmtOne(startDate) : `${fmtOne(startDate)} - ${fmtOne(endDate)}`;
}
